import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useCallGraph } from '@/components/smartai/api/getCallGraph';
import type { CompareResponse } from '@/components/smartai/api/compareBuildDiff';
import { registerTestIntelligenceScanSession } from '@/components/smartai/api/registerScanSession';
import MethodsTab from './MethodsTab';
import TestRunSpecsTab from './TestRunSpecsTab';
import { useTestRunSpecs } from '@/components/smartai/api/testRunSpecs';
import {
  testOptBodyClass,
  testOptPrimaryTab,
  testOptPrimaryTabCount,
  testOptPrimaryTabsClass,
  testOptRootClass,
  testOptRootEmbeddedClass,
  testOptScanIdClass,
  testOptTitleClass,
  testOptTopbarClass,
} from './test-optimization-layout';

export type TestOptTabId = 'methods' | 'test-run-specs';

export type TestOptimizationPanelProps = {
  scanId: string;
  codeRepositoryMongoId: string;
  repoUrl?: string;
  branchName?: string;
  patToken?: string;
  /** Renders inside Build Diff (no standalone page chrome). */
  embedded?: boolean;
  /** ADO build compare result — drives Methods tab when embedded in Build Diff. */
  diffData?: CompareResponse | null;
};

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as {
      response?: { data?: { detail?: unknown; message?: string } };
      message?: string;
    };
    const detail = axiosErr.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (axiosErr.response?.data?.message) return axiosErr.response.data.message;
    if (axiosErr.message) return axiosErr.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

const TestOptimizationPanel = ({
  scanId,
  embedded = false,
  diffData = null,
}: TestOptimizationPanelProps) => {
  const isBuildDiffContext = Boolean(embedded &&diffData);
  const buildToScanId = diffData?.scanB?.scanId?.trim() ?? '';
  const effectiveScanId = (isBuildDiffContext && buildToScanId) || scanId.trim();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TestOptTabId>('methods');
  const [sessionBootstrapped, setSessionBootstrapped] = useState(false);

  const needsCallGraph = Boolean(effectiveScanId);
  useEffect(() => {
    if (!effectiveScanId || !needsCallGraph) {
      if (!needsCallGraph) setSessionBootstrapped(true);
      return;
    }
    let cancelled = false;
    setSessionBootstrapped(false);
    void registerTestIntelligenceScanSession(effectiveScanId)
      .then(() => {
        if (!cancelled) {
          void queryClient.invalidateQueries({
            queryKey: ['call-graph', effectiveScanId],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setSessionBootstrapped(true);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveScanId, needsCallGraph, queryClient]);

  const {
    data: graphPayload,
    isPending: graphLoading,
    isError: graphLoadError,
    error: graphError,
  } = useCallGraph(effectiveScanId, Boolean(effectiveScanId) && needsCallGraph);

  const { data: specsData } = useTestRunSpecs(
    effectiveScanId,
    Boolean(effectiveScanId),
  );
  const specsCount = specsData?.total ?? 0;

  const diffMethods = diffData?.testOptimizationMethods ?? [];
  const allMethodsCount = diffData
    ? (diffData.testOptimizationSummary?.diffEntityCount ?? diffMethods.length)
    : Object.values(graphPayload?.entitiesByFile ?? {}).reduce(
      (n, list) => n + list.length,
      0,
    );

  const graphErrorMessage = graphLoadError
    ? getApiErrorMessage(graphError, 'Failed to load graph data')
    : undefined;

  const methodsLoading = isBuildDiffContext
    ? false
    : !sessionBootstrapped || graphLoading;
  const loading = activeTab === 'methods' ? methodsLoading : false;

  const primaryTabs: { id: TestOptTabId; label: string; count?: number }[] = [
    { id: 'methods', label: 'Methods', count: allMethodsCount || undefined },
    {
      id: 'test-run-specs',
      label: 'Test Run Specs',
      count: specsCount || undefined,
    },
  ];

  return (
    <div className={embedded ? testOptRootEmbeddedClass : testOptRootClass}>
      {!embedded && (
        <header className={testOptTopbarClass}>
          <div>
            <h1 className={testOptTitleClass}>Test optimization</h1>
            <p className={testOptScanIdClass}>
              Scan <code>{effectiveScanId}</code>
              {diffData?.scanB?.buildNumber && (
                <>
                  {' '}
                  · Build B <code>{diffData.scanB.buildNumber}</code>
                </>
              )}
            </p>
          </div>
        </header>
      )}


      <nav
        className={testOptPrimaryTabsClass}
        aria-label="Test optimization views">
        {primaryTabs.map(tab => (
          <Button
            key={tab.id}
            type="button"
            variant="ghost"
            className={testOptPrimaryTab(activeTab === tab.id)}
            onClick={() => setActiveTab(tab.id)}
            aria-current={activeTab === tab.id ? 'page' : undefined}>
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span className={testOptPrimaryTabCount(activeTab === tab.id)}>
                {tab.count}
              </span>
            )}
          </Button>
        ))}
      </nav>

      <div className={testOptBodyClass}>
        {activeTab === 'methods' && (
          <MethodsTab
            payload={graphPayload}
            loading={methodsLoading}
            errorMessage={isBuildDiffContext ? undefined : graphErrorMessage}
            embedded={embedded}
            scanId={effectiveScanId}
            diffData={diffData}
            onSpecSaved={() => {
              void queryClient.invalidateQueries({
                queryKey: ['test-run-specs', effectiveScanId],
              });
              setActiveTab('test-run-specs');
            }}
          />
        )}

        {activeTab === 'test-run-specs' && (
          <TestRunSpecsTab
            scanId={effectiveScanId}
            loading={loading}
            onCreateNew={() => setActiveTab('methods')}
          />
        )}
      </div>

    </div>
  );
};

export default TestOptimizationPanel;
